import React, {
  forwardRef,
  KeyboardEvent,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  checkIsMultiple,
  getInitPopUpData,
  getMultiPopupColumns,
} from "@shared/utils/CommonUtils";
import { fnGetDataSet } from "@shared/api/restApi";
import SearchPanel from "../SearchPanel";
import Button from "@mui/material/Button";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { global } from "@shared/libs/Global";
import CommonSpinner from "@widgets/CommonSpinner";
import { Badge, IconButton, Typography } from "@mui/material";
import { ArrowLeftIcon } from "@mui/x-date-pickers";
import { v4 as uuidv4 } from "uuid";
import { PopupSettings } from "@widgets/commonpopup/PopupSettings";

interface MultiPopupProps {
  code: string;
  selectedData?: any;
  modalRef?: any;
  options?: any[];
  autoSearch?: boolean;
}

const MultiPopup = forwardRef(
  (
    {
      code,
      selectedData,
      options,
      modalRef,
      autoSearch = true,
    }: MultiPopupProps,
    ref
  ) => {
    const { userInfo } = global();

    const searchRef = useRef<any>(null);
    const gridApiRefH = useRef<any>();
    const gridApiRefL = useRef<any>();

    const [formData, setFormData] = useState<any>();
    const [formControlData, setFormControlData] = useState<any[]>([]);
    const [columns, setColums] = useState<any[]>([]);
    const [columns2, setColums2] = useState<any[]>([]);
    const [dataSource1, setDataSource1] = useState<any[]>([]);
    const [dataSource2, setDataSource2] = useState<any[]>([]);
    const [doubleData, setDoubleData] = useState<any[]>();
    const [isLoading, setIsLoading] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const usedKeys = new Set<string>();

    // CommonUtils에서 isMultiple 속성 조회
    const isMultiple = checkIsMultiple(code) || false;

    useImperativeHandle(ref, () => ({
      getSelectedRow,
      onSearch,
    }));

    useEffect(() => {
      if (doubleData) {
        modalRef.current?.handleConfirm(true);
      }
    }, [doubleData]);

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUpData(code, {});
          const excludedKeys = PopupSettings[code]?.excludeBindKeys ?? [];
          const safeSelectedData = Object.fromEntries(
            Object.entries(selectedData ?? {}).filter(
              ([key]) => !excludedKeys.includes(key)
            )
          );
          const mergedData = { ...rtn.formData, ...safeSelectedData };
          setFormData(mergedData);
          setFormControlData(rtn.formControlData);
          setColums(rtn.grid.grid1);

          let mappedCols: any[] = [];

          const deleteBtn = {
            cd_firm: userInfo.cd_firm,
            cd_menu: code,
            cd_grid: "grid1",
            cd_user: "admin",
            cd_column: "btn_delete",
            nm_column: "",
            tp_column: "SnButtonBox",
            fg_visible: "Y",
            cd_displaycolumn: null,
            cd_clas: null,
            cd_popup: null,
            no_seq: 0,
            qt_width: 40,
            yn_required: "N",
            yn_allowsorting: "Y",
            yn_fixed: "N",
            tp_fixedposition: null,
            qt_groupindex: -1,
            yn_sum: "N",
            yn_system: "N",
            tp_format: null,
            dc_editcon: null,
            yn_drilldown: "N",
            dc_sql: null,
            dc_filter: null,
            headerComponentParams: {
              iconType: "btn_delete",
              onClick: removeCartAll,
            },
            cellRendererParams: {
              iconType: "btn_delete",
              onClick: removeFromCart,
            },
          };
          mappedCols.push(deleteBtn);

          const columnsList = getMultiPopupColumns(code);
          const grid1 = rtn.grid.grid1;

          grid1.map((item: any) => {
            if (columnsList.includes(item.cd_column)) {
              mappedCols.push(item);
            }
          });

          setColums2(mappedCols);

          if (mergedData && mergedData.str_search) {
            // 초기화 시 조회조건 있으면 → SearchPanel(조회조건)에 포커스 유지
            onSearch(mergedData, true);
          } else {
            // 초기화 시 조회조건 없으면 → 자동검색 후 그리드로 포커스 이동
            if (autoSearch) onSearch(mergedData, false);
          }
        } catch (error) {
          console.log(error);
        }
      };
      fetchform();
      if (options && options.length > 0) {
        setDataSource2(options);
      }
    }, [userInfo]);

    // 그리드에 행이 렌더될 때까지 기다렸다가 첫 행을 선택/포커스
    const focusGridFirstRow = (attempt: number = 0) => {
      const api = gridApiRefH.current;
      if (!api) return;
      if ((api.getDisplayedRowCount?.() ?? 0) > 0) {
        api.selectAndFocusRow(0, false);
        return;
      }
      if (attempt < 30) {
        requestAnimationFrame(() => focusGridFirstRow(attempt + 1));
      }
    };

    const onSearch = async (mergedData: any, keepFocus: boolean = false) => {
      setIsLoading(true);
      let rowCount = 0;
      const fetchdatasource = async () => {
        try {
          const params = {
            id: code.toLowerCase() + "_01",
            params: mergedData ? mergedData : searchRef.current.getFormdata(),
          };

          setDataSource1([]);
          const data: any = await fnGetDataSet(params);
          const rows = data[0] || [];
          rowCount = rows.length;
          setDataSource1(rows);
          gridApiRefH.current.resetUpdate();
        } catch (error) {
          console.log(error);
        }
      };
      await fetchdatasource();
      setIsLoading(false);
      // 조회 결과가 있고 그리드로 이동해야 하면 첫 행 포커스,
      // 그 외(조회조건 유지 or 결과 0건)에는 SearchPanel 포커스 유지
      if (!keepFocus && rowCount > 0) {
        focusGridFirstRow();
      } else {
        searchRef.current?.focusLastInput?.();
      }
    };

    // grid의 KeyField 생성해주는 메서드
    const generateKey = () => {
      let key;
      do {
        key = uuidv4(); // 항상 새로운 UUID 생성
      } while (usedKeys.has(key)); // 중복 여부 확인
      usedKeys.add(key);
      return key;
    };

    // + 버튼 클릭: 체크된 데이터를 장바구니(grid2)에 추가
    const addToCart = () => {
      const selectedData = gridApiRefH.current.getCheckedRows();
      if (!selectedData || selectedData.length === 0) return;

      if (isMultiple) {
        setDataSource2((prev) => [
          ...prev,
          ...selectedData.map((row: any) => ({
            ...row,
            KeyField: generateKey(),
          })),
        ]);
      } else {
        // 첫번째 보이는 필드를 가져옴
        const firstVisibleColumn = columns
          ?.filter((col: any) => col.fg_visible === "Y")
          ?.reduce((minCol: any, currentCol: any) => {
            if (!minCol) return currentCol;
            return Number(currentCol.no_seq) < Number(minCol.no_seq)
              ? currentCol
              : minCol;
          }, null);
        const keyColumnField = firstVisibleColumn
          ? firstVisibleColumn.cd_column
          : null;

        setDataSource2((prev) => {
          const newData = [...prev];

          selectedData.forEach((row: any) => {
            // 첫 번째 컬럼 필드를 찾았다면 그 값을 기준으로 비교
            const isDuplicate = newData.some((item) => {
              if (keyColumnField) {
                return item[keyColumnField] === row[keyColumnField];
              }
            });

            if (!isDuplicate) {
              newData.push(row);
            }
          });
          return newData;
        });
      }

      if (selectedData && selectedData[0].fixCheckbox) {
        gridApiRefH.current?.forEachFilteredNode((node: any) => {
          node.setDataValue("fixCheckbox", false);
        });
      }
    };

    // grid2에서 클릭한 데이터 제거
    const removeFromCart = (rowData: any) => {
      if (!rowData) return;
      setDataSource2((prev) =>
        prev.filter((row) => row.KeyField !== rowData.KeyField)
      );
    };

    // grid2의 모든 데이터 삭제
    const removeCartAll = () => {
      setDataSource2([]);
    };

    const onRowDoubleClicked = (e: any) => {
      const returnData: any[] = [...dataSource2];
      if (isMultiple) {
        returnData.push(e.data);
      } else {
        // e.data가 dataSource2에 없는 경우에만 추가
        const exists = returnData.some((data) => data.id === e.data.id);
        if (!exists) returnData.push(e.data);
      }
      setDataSource2(returnData);
      setDoubleData(returnData);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        const focusedNode = gridApiRefH.current.focusedRowNode();
        if (focusedNode) {
          onRowDoubleClicked({ data: focusedNode.data });
        }
      }
    };

    const getSelectedRow = () => {
      if (!dataSource2 || dataSource2.length === 0) {
        const checkedRows = gridApiRefH.current.getCheckedFilteredRows();
        if (checkedRows.length > 0) {
          return checkedRows;
        }
        return gridApiRefH.current?.getSelectedRows() || [];
      }
      return dataSource2;
    };

    return (
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflowX: "hidden",
        }}
      >
        {isLoading && <CommonSpinner isLoading={isLoading} />}
        <div>
          <SearchPanel
            ref={searchRef}
            data={formData}
            controls={formControlData}
            popupRef={ref}
          ></SearchPanel>
        </div>
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            marginTop: 8,
            marginBottom: 0,
          }}
        >
          {/* 버튼 그룹 */}
          <div style={{ display: "flex", alignItems: "center" }}>
            {/* 좌측 추가 버튼 */}
            <Button
              type="button"
              variant="outlined"
              onClick={addToCart}
              sx={{
                minWidth: 0,
                width: 32,
                height: 32,
                marginLeft: 1,
              }}
            >
              +
            </Button>
          </div>

          {/* 중앙 조회 버튼 */}
          <div
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={() => onSearch(undefined)}
              sx={{ width: 100 }}
            >
              조회
            </Button>
          </div>

          {/* 우측 토글 버튼 */}
          {!drawerOpen && (
            <div
              style={{
                flex: 1,
                textAlign: "right",
                marginRight: dataSource2.length > 0 ? "20px" : "0px",
              }}
            >
              <Badge
                badgeContent={dataSource2.length} // 장바구니 row 수
                color="error" // 빨간색
                overlap="circular"
                showZero={false}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                sx={{
                  "& .MuiBadge-badge": {
                    transform: "translate(100%, -80%)",
                  },
                }}
              >
                <IconButton
                  onClick={() => setDrawerOpen(true)}
                  sx={{
                    width: 24,
                    height: 24,
                    border: "1px solid #ccc",
                    background: "#fff",
                  }}
                >
                  <ArrowLeftIcon fontSize="small" />
                </IconButton>
              </Badge>
            </div>
          )}
        </div>
        <div
          style={{
            display: "flex",
            flex: 1,
            minHeight: 0,
            marginTop: 10,
          }}
        >
          <div style={{ flex: 1, minHeight: 0 }} onKeyDown={handleKeyDown}>
            <SnGrid
              ref={gridApiRefH}
              columnConfig={columns} // 컬럼 설정 전달
              rowData={dataSource1}
              etcProps={{
                rowSelection: "multiple",
                isEditable: false,
                ischeckboxSelection: true,
                // 조회 직후 그리드가 포커스를 뺏지 않도록 항상 억제하고,
                // 포커스 이동은 팝업(onSearch)에서 명시적으로 제어한다.
                suppressInitialCellFocus: true,
              }}
              events={{
                onRowDoubleClicked: onRowDoubleClicked,
              }}
            />
          </div>
        </div>

        {/* 장바구니 panel */}
        {drawerOpen && (
          <>
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                height: "100%",
                width: 400,
                backgroundColor: "#fff",
                display: "flex",
                flexDirection: "column",
                zIndex: 10,
                padding: 10,
                border: "1px solid rgb(186,191,199)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography variant="h6">장바구니</Typography>
              <div style={{ flex: 1, minHeight: 0, marginTop: 5 }}>
                <SnGrid
                  ref={gridApiRefL}
                  columnConfig={columns2}
                  rowData={dataSource2}
                  etcProps={{ rowSelection: "multiple", isEditable: false }}
                />
              </div>
            </div>

            <div
              onClick={() => setDrawerOpen(false)}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 400,
                bottom: 0,
                zIndex: 9,
              }}
            />
          </>
        )}
      </div>
    );
  }
);

export default MultiPopup;
